import React from 'react'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'
import Head from 'next/head'
import { getFacultiesData } from '~shared/api/faculties/getFacultiesData'
import { getJournalsData } from '~shared/api/getJournalsData/getJournalsData'
import { getFooterData } from '~shared/api/footer/getFooterData'
import { getPopularPrograms } from '~shared/api/programs-popular/getPopularPrograms'
import { getCategoriesData } from '~shared/api/categories/getCategories'
import { HeaderUpdated } from '~widgets/header/components/header-updated'
import { Footer } from '~widgets/footer/components/footer'
import { Breadcrumbs } from '~widgets/breadcrumbs/breadcrumbs'
import { JournalsSection } from '~pages/journals-page/journals-section'
import { PATHS } from '~shared/constants/paths'
import { useAt } from '~shared/hooks/useAt'
import { getJournalPath } from '~shared/utils/getJournalPath'

export type TJournalsPageProps = {
  faculties: Awaited<Promise<ReturnType<typeof getFacultiesData>>>
  journals: Awaited<Promise<ReturnType<typeof getJournalsData>>>
  footer: Awaited<ReturnType<typeof getFooterData>>
  popularPrograms: Awaited<Promise<ReturnType<typeof getPopularPrograms>>>
  categories: Awaited<ReturnType<typeof getCategoriesData>>
}

export const JournalsPage = ({
  faculties,
  categories,
  popularPrograms,
  footer,
  journals
}: TJournalsPageProps) => {
  const { currentJournalsFacultySlug } = useAt()
  const { locale, query } = useRouter()
  // query.tag — новый роут /journal/{tag}, useAt — старый /journal--{tag}
  const currentTagSlug = query.tag?.toString() ?? currentJournalsFacultySlug
  const currentFacultyName = faculties?.find(
    faculty => faculty.slug === currentTagSlug
  )?.name
  const toDativeCase = (word: string) => {
    const lastChar = word.charAt(word.length - 1)
    const preLastChar = word.charAt(word.length - 2)
    if (preLastChar === 'а' || preLastChar === 'я') {
      return `${word.slice(0, -2)}е`
    }
    if (preLastChar === 'и' && lastChar === 'е') {
      return `${word.slice(0, -2)}ию`
    }
    if (preLastChar === 'а' && lastChar === 'я') {
      return `${word.slice(0, -2)}ой`
    }
    if (lastChar === 'а') {
      return `${word.slice(0, -1)}е`
    }
    if (lastChar === 'й') {
      return `${word.slice(0, -1)}ю`
    }
    if (lastChar === 'о') {
      return `${word.slice(0, -1)}у`
    }
    if (lastChar === 'ь' || lastChar === 'я') {
      return `${word.slice(0, -1)}и`
    }
    return `${word}у`
  }
  const seoParams = {
    title:
      (currentTagSlug &&
        locale === 'ru' &&
        `Статьи по ${(currentFacultyName ?? 'Факультету')
          .split(' ')
          .filter(name => name !== 'Про')
          .map(name => (name.length > 3 ? toDativeCase(name) : name))
          .join(' ')} | Блог MITM.institute`) ||
      'Журнал | Московский Институт Технологий и Управления',
    desc:
      (currentTagSlug &&
        `Интересные информационные статьи и публикации по теме "${currentFacultyName}" от онлайн-института MITM. Читайте в журнале актуальные материалы, последние тренды , полезные практики и обзоры.`) ||
      'Журнал MITM.institute ➜ Статьи о дизайне, психологии, экономике, прикладной информатике, менеджменте, повышении квалификации, образовании и профессиональном росте.',
    canonical: `${PATHS.front.root.index}${getJournalPath(currentTagSlug)}`
  }

  return (
    <>
      <NextSeo
        title={seoParams.title}
        description={seoParams.desc}
        canonical={seoParams.canonical}
        openGraph={{
          url: seoParams.canonical,
          title: seoParams.title,
          description: seoParams.desc,
          images: [
            {
              url: `${PATHS.front.root.index}${PATHS.front.public.assets.imgs.icons.mitmLogo512.index}`,
              width: 512,
              height: 512,
              alt: 'Московский Институт Технологий и Управления',
              type: 'image/png'
            }
          ],
          site_name: 'Московский Институт Технологий и Управления'
        }}
      />
      {locale !== 'ru' && (
        <Head>
          <meta name='robots' content='noindex, nofollow' />
        </Head>
      )}

      <HeaderUpdated
        categories={categories}
        popularPrograms={popularPrograms}
      />
      <main>
        <Breadcrumbs
          items={[
            {
              label: 'Журнал',
              href: '/journal'
            },
            { label: currentFacultyName ?? '' }
          ]}
        />
        <JournalsSection faculties={faculties} journals={journals} />
      </main>
      <Footer data={footer} />
    </>
  )
}
